import { useContext, useMemo } from 'react'
import cn from 'classnames/bind'
import moment from 'moment-timezone'
import { isEmpty, values, map, pipe, sort, prop, descend } from 'ramda'
import { useTranslation } from 'react-i18next'

import EmptyWarn from 'app/common/layouts/EmptyWarn'
import Loading from 'app/common/components/Loader'
import toFixedValue from 'app/common/lib/toFixedValue'
import { BannerContent } from 'app/common/components-next/banner'
import { Table, Tr, Td, Th, useCopyColumnsWidths } from 'app/common/ui-components/table'
import OveflowedText from 'app/common/components/OveflowedText'
import { useCurrencySign } from 'app/common/components-next/currency'
import Sticky from 'app/common/templates-next/sticky'
import { PageTemplateContent } from 'app/common/templates-next/page-template'
import PlainTooltip from 'app/common/ui-next/plain-tooltip'

import { Purchases } from '../PurchasesApi'
import ComplexTooltip from './ComplexTooltip'
import Individual from '../commonComponents/Individual'
import styles from './PurchasesTable.module.scss';

const cx = cn.bind(styles)

type Props = {
  timezone: string
  hideSomeColumns?: boolean
}

const PurchasesTable = ({ timezone, hideSomeColumns }: Props) => {
  if (!timezone) {
    console.error("PurchasesTable — не пришел обязательный параметр 'timezone'") // eslint-disable-line no-console
  }
  const { t } = useTranslation('cafeteria')
  const currency = useCurrencySign('commaSpaceBefore')
  const {
    content: { purchases },
    loading,
  } = useContext(Purchases)
  const tableHeaders = t('purchases_table_columns', { returnObjects: true })

  const purchaseItems = useMemo(
    () =>
      pipe(
        values,
        map(({ datetime, individual, items, guid, total, number }: any) => {
          const mDate = moment(datetime).tz(timezone)
          const date = mDate.format('DD.MM')
          const time = mDate.format('HH:mm')
          return {
            guid,
            number,
            date,
            time,
            seconds: mDate.unix(),
            individual,
            total: toFixedValue(total),
            items: map(
              ({ id, n, title, cost: itemCost, type, items: complexItems }) => ({
                id,
                itemsNumber: n,
                title,
                cost: toFixedValue(itemCost),
                type,
                complexItems,
              }),
              values(items),
            ),
          }
        }),
        sort(descend(prop('seconds'))),
      )(purchases),
    [purchases, timezone],
  )

  const empty = isEmpty(purchases)

  const [refFrom, refTo] = useCopyColumnsWidths()

  return (
    <>
      <Sticky>
        {(ref, sticky, shadow) => (
          <PageTemplateContent
            className={cx({ sticky })}
            shadowBottom={shadow}
            reference={ref}
            skipPadding
            flex
          >
            <div className={styles.tableContainer}>
              <Table className={styles.table}>
                <thead>
                  <Tr header reference={refTo} headerHasBorderBottom={false}>
                    <Th alignLeft id="header1" className={cx(styles.th, styles.thDate)}>
                      {tableHeaders[0]}
                    </Th>
                    <Th alignLeft id="header2" className={styles.th}>
                      {tableHeaders[1]}
                    </Th>
                    {!hideSomeColumns && (
                      <Th alignLeft id="header3" className={styles.th}>
                        {tableHeaders[2]}
                      </Th>
                    )}
                    {!hideSomeColumns && (
                      <Th alignLeft id="header4" className={styles.th}>
                        {tableHeaders[3]}
                      </Th>
                    )}
                    <Th alignLeft id="header5" className={styles.th}>
                      {tableHeaders[4]}
                    </Th>
                    <Th alignRight id="header6" className={styles.th}>
                      {tableHeaders[5]}
                      {currency}
                    </Th>
                    <Th alignRight id="header7" className={styles.th}>
                      {tableHeaders[6]}
                    </Th>
                    <Th alignRight id="header8" className={styles.th}>
                      {tableHeaders[7]}
                      {currency}
                    </Th>
                  </Tr>
                </thead>
              </Table>
            </div>

            <BannerContent
              placeId="d099e4c6-f44b-42ef-b5f9-a1d52c27dee5"
              className={styles.bannerPlaceholder}
              displayAsPlaceholder
              height={0}
            />
          </PageTemplateContent>
        )}
      </Sticky>

      <PageTemplateContent className={styles.layout} skipPadding flex>
        <div className={styles.tableContainer}>
          {loading && <Loading />}

          {!loading && empty && (
            <EmptyWarn>{t('purchases_table_empty', 'Нет покупок за выбранный период')}</EmptyWarn>
          )}

          {!loading && (
            <Table className={styles.table}>
              <thead>
                <Tr header hidden reference={refFrom}>
                  <Th alignLeft className={styles.th}>
                    {tableHeaders[0]}
                  </Th>
                  <Th alignLeft className={styles.th}>
                    {tableHeaders[1]}
                  </Th>
                  {!hideSomeColumns && (
                    <Th alignLeft className={styles.th}>
                      {tableHeaders[2]}
                    </Th>
                  )}
                  {!hideSomeColumns && (
                    <Th alignLeft className={styles.th}>
                      {tableHeaders[3]}
                    </Th>
                  )}
                  <Th alignLeft className={styles.th}>
                    {tableHeaders[4]}
                  </Th>
                  <Th alignRight className={styles.th}>
                    {tableHeaders[5]}
                    {currency}
                  </Th>
                  <Th alignRight className={styles.th}>
                    {tableHeaders[6]}
                  </Th>
                  <Th alignRight className={styles.th}>
                    {tableHeaders[7]}
                    {currency}
                  </Th>
                </Tr>
              </thead>
              <tbody>
                {purchaseItems.map(({ guid, number, date, time, individual, total, items }) => (
                  <Tr key={guid} className={styles.row}>
                    <Td headers="header1" className={styles.tdDate}>
                      {date}
                    </Td>
                    <Td headers="header2">{time}</Td>
                    {!hideSomeColumns && <Td headers="header3">{number}</Td>}
                    {!hideSomeColumns && (
                      <Td headers="header4">
                        <Individual individual={individual} />
                      </Td>
                    )}
                    <Td headers="header5" className={styles.itemsCell}>
                      <ul className={styles.list}>
                        {items.map(({ id, title, type, complexItems }) =>
                          type === 1 ? (
                            <li key={id} className={styles.complexTitle}>
                              <ComplexTooltip items={complexItems} title={title}>
                                {title}
                              </ComplexTooltip>
                            </li>
                          ) : (
                            <li key={id} className={styles.dishTitle}>
                              <PlainTooltip text={title}>{title}</PlainTooltip>
                            </li>
                          ),
                        )}
                      </ul>
                    </Td>
                    <Td alignRight headers="header6">
                      <ul className={styles.list}>
                        {items.map(({ id, cost }) => (
                          <li key={id}>
                            <OveflowedText className={styles.dishCost}>{cost}</OveflowedText>
                          </li>
                        ))}
                      </ul>
                    </Td>
                    <Td alignRight headers="header7">
                      <ul className={styles.list}>
                        {items.map(({ id, itemsNumber }) => (
                          <li key={id}>
                            <OveflowedText className={styles.dishNumber}>
                              {itemsNumber}
                            </OveflowedText>
                          </li>
                        ))}
                      </ul>
                    </Td>
                    <Td alignRight headers="header8">
                      {total}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>

        <BannerContent
          // leaveContainerOnEmpty
          refreshOnMount
          className={styles.bannerContent}
          placeId="d099e4c6-f44b-42ef-b5f9-a1d52c27dee5"
        />
      </PageTemplateContent>
    </>
  )
}

export default PurchasesTable
